import { gql, useQuery } from '@apollo/client';

/**
 * The signed-in user's TinyCMS sites (a site is an account holding TinyCMS),
 * for the header's site switcher and the All sites page ("TinyCMS UX
 * optimization review v6.zip", Prototype v3).
 */

export const TINYCMS_SITES = gql`
  query TinycmsSites {
    tinycmsSites {
      accountId
      name
      role
      plan
      status
      host
      siteUrl
      iconUrl
      waiting
      lastPublishedAt
      ownerEmail
      createdAt
    }
  }
`;

export const CREATE_TINYCMS_SITE = gql`
  mutation CreateTinycmsSite($input: CreateTinycmsSiteInput!) {
    createTinycmsSite(input: $input) {
      id
      name
    }
  }
`;

export interface SiteSummary {
  accountId: string;
  name: string;
  role: string;
  plan: string | null;
  status: string;
  host: string | null;
  siteUrl: string | null;
  iconUrl: string | null;
  waiting: number;
  lastPublishedAt: string | null;
  ownerEmail: string | null;
  createdAt: string;
}

export function useTinycmsSites(skip = false) {
  const { data, loading, refetch } = useQuery<{ tinycmsSites: SiteSummary[] }>(TINYCMS_SITES, {
    skip,
    fetchPolicy: 'cache-and-network',
  });
  return { sites: data?.tinycmsSites || [], loading, refetch };
}

/** A steady hue per site, for its initial tile and card tint. */
export function siteHue(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
}

export const roleLabel = (role: string) =>
  role === 'admin' ? 'Owner' : role ? role[0].toUpperCase() + role.slice(1) : 'Member';

/** "Business plan"; null for an account with no TinyCMS plan on record. */
export const planLabel = (plan: string | null) =>
  plan ? `${plan[0].toUpperCase()}${plan.slice(1)} plan` : null;

/** "Published 2:41 PM" · "Published yesterday" · "Published Oct 8" · "Nothing published yet". */
export function publishedLine(at: string | null, now = new Date()) {
  if (!at) return 'Nothing published yet';
  const d = new Date(at);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  if (diff === 0)
    return `Published ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  if (diff === 1) return 'Published yesterday';
  return `Published ${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  })}`;
}
